import type { CashSummary } from '../../cash/infra/cashSummaryTypes';
import type { NetworkCallCategory } from '../../shared/dev/networkCallAudit';
import { recordNetworkCall } from '../../shared/dev/networkCallAudit';

export const REPORTS_REMOTE_CACHE_MS = 5 * 60 * 1000;

type CacheEntry<T> = { key: string; value: T; fetchedAt: number };

const cache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

export function reportsCacheKey(parts: (string | number | null | undefined)[]): string {
  return parts.map((p) => (p == null ? '' : String(p))).join('|');
}

export function getReportsCacheIfFresh<T>(key: string, maxAgeMs = REPORTS_REMOTE_CACHE_MS): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt >= maxAgeMs) return null;
  return entry.value as T;
}

export function setReportsCache<T>(key: string, value: T): void {
  cache.set(key, { key, value, fetchedAt: Date.now() });
}

export function invalidateReportsCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

export async function withReportsCache<T>(
  key: string,
  fetcher: () => Promise<T | null>,
  maxAgeMs = REPORTS_REMOTE_CACHE_MS,
  audit?: { category: NetworkCallCategory; name: string }
): Promise<T | null> {
  const cached = getReportsCacheIfFresh<T>(key, maxAgeMs);
  if (cached != null) {
    if (audit) {
      recordNetworkCall({ category: audit.category, name: audit.name, cached: true });
    }
    return cached;
  }

  const existing = inFlight.get(key);
  if (existing) {
    return existing as Promise<T | null>;
  }

  const promise = (async () => {
    if (audit) {
      recordNetworkCall({ category: audit.category, name: audit.name });
    }
    return fetcher();
  })()
    .then((value) => {
      if (value != null) {
        setReportsCache(key, value);
      }
      return value;
    })
    .finally(() => {
      if (inFlight.get(key) === promise) {
        inFlight.delete(key);
      }
    });

  inFlight.set(key, promise);
  return promise;
}

/** Cash summary comparte TTL 60s (alias semántico). */
export const CASH_SUMMARY_REPORTS_CACHE_MS = 60_000;

export type CachedCashSummary = CashSummary;
